// src/utils/enhancedTokenManager.js - Enhanced with Remember Me functionality
const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

class EnhancedTokenManager {
  constructor() {
    this.refreshPromise = null;
    this.isRefreshing = false;
    this.tokenKey = 'token';
    this.userKey = 'user';
    this.rememberMeKey = 'rememberMe';
  }

  // 🔥 NEW: Enhanced token storage with remember me support
  getToken() {
    // Check if Remember Me is enabled
    const rememberMe = this.isRememberMeEnabled();
    
    if (rememberMe) {
      // If Remember Me is ON, only use localStorage
      return localStorage.getItem(this.tokenKey);
    } else {
      // If Remember Me is OFF, prioritize sessionStorage but fallback to localStorage
      const sessionToken = sessionStorage.getItem(this.tokenKey);
      const localToken = localStorage.getItem(this.tokenKey);
      return sessionToken || localToken;
    }
  }

  // 🔥 NEW: Set token with remember me option
  setToken(token, rememberMe = false, refreshToken = null) {
    
    try {
      // ALWAYS store in localStorage as fallback
      localStorage.setItem(this.tokenKey, token);
      
      if (!rememberMe) {
        // If Remember Me is OFF, also store in sessionStorage for this session
        sessionStorage.setItem(this.tokenKey, token);
      } else {
        // If Remember Me is ON, clear from sessionStorage
        sessionStorage.removeItem(this.tokenKey);
      }
      
      // Store refresh token if provided
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
        if (!rememberMe) {
          sessionStorage.setItem('refreshToken', refreshToken);
        }
      }
      
      // Store remember me preference in localStorage
      localStorage.setItem(this.rememberMeKey, JSON.stringify(rememberMe));
      
      // Verify storage
      const verifyLocal = localStorage.getItem(this.tokenKey);
      const verifySession = sessionStorage.getItem(this.tokenKey);
      
      if (rememberMe && verifyLocal === token) {
      } else if (!rememberMe && (verifySession === token || verifyLocal === token)) {
      } else {
      }
      
    } catch (error) {
      // Fallback: try to store in at least one location
      try {
        localStorage.setItem(this.tokenKey, token);
      } catch (fallbackError) {
      }
    }
  }

  // 🔥 NEW: Check if remember me was enabled
  isRememberMeEnabled() {
    try {
      return JSON.parse(localStorage.getItem(this.rememberMeKey)) || false;
    } catch {
      return false;
    }
  }

  // 🔥 NEW: Set user data with appropriate storage
  setUserData(userData, rememberMe = null) {
    // Use provided rememberMe or check stored preference
    const useRememberMe = rememberMe !== null ? rememberMe : this.isRememberMeEnabled();
    
    try {
      const userDataString = JSON.stringify(userData);
      
      // ALWAYS store in localStorage as fallback
      localStorage.setItem(this.userKey, userDataString);
      
      if (!useRememberMe) {
        // If Remember Me is OFF, also store in sessionStorage
        sessionStorage.setItem(this.userKey, userDataString);
      } else {
        // If Remember Me is ON, clear from sessionStorage
        sessionStorage.removeItem(this.userKey);
      }
      
      // Verify storage
      const verifyLocal = localStorage.getItem(this.userKey);
      const verifySession = sessionStorage.getItem(this.userKey);
      
      if (useRememberMe && verifyLocal) {
      } else if (!useRememberMe && (verifySession || verifyLocal)) {
      } else {
      }
      
    } catch (error) {
      // Fallback: try to store in at least one location
      try {
        localStorage.setItem(this.userKey, JSON.stringify(userData));
      } catch (fallbackError) {
      }
    }
  }

  // 🔥 NEW: Get user data from appropriate storage
  getUserData() {
    // Check if Remember Me is enabled
    const rememberMe = this.isRememberMeEnabled();
    
    let userData = null;
    if (rememberMe) {
      // If Remember Me is ON, only use localStorage
      userData = localStorage.getItem(this.userKey);
    } else {
      // If Remember Me is OFF, prioritize sessionStorage but fallback to localStorage
      const sessionUser = sessionStorage.getItem(this.userKey);
      const localUser = localStorage.getItem(this.userKey);
      userData = sessionUser || localUser;
    }
    
    return userData ? JSON.parse(userData) : null;
  }

  // 🔥 ENHANCED: Remove token with remember me awareness
  removeToken() {
    // Clear from both storages since we now store in both places
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.rememberMeKey);
    localStorage.removeItem('refreshToken');
    
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.userKey);
    sessionStorage.removeItem('refreshToken');
    
  }

  // Decode JWT payload (unchanged)
  decodeToken(token) {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      return null;
    }
  }

  // Check if token is expired or will expire soon (unchanged)
  shouldRefresh(token) {
    if (!token) return false;
    
    const payload = this.decodeToken(token);
    if (!payload) return false;
    
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - currentTime;
    
    // Refresh if token expires within 5 minutes (300 seconds)
    return timeUntilExpiry < 300;
  }

  isTokenExpired(token) {
    if (!token) return true;
    
    const payload = this.decodeToken(token);
    if (!payload) return true;
    
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  }

  // 🔥 ENHANCED: Refresh token with remember me awareness
  async refreshToken() {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    
    this.refreshPromise = new Promise(async (resolve, reject) => {
      try {
        const currentToken = this.getToken();
        if (!currentToken) {
          throw new Error('No token to refresh');
        }

        // Get current remember me preference
        const rememberMe = this.isRememberMeEnabled();

        const response = await fetch(`${API_URL}/api/auth/refresh-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Token refresh failed');
        }

        const data = await response.json();
        
        // 🔥 NEW: Store refreshed token with same remember me preference
        this.setToken(data.token, rememberMe);
        
        resolve(data.token);
      } catch (error) {
        this.handleExpiredToken();
        reject(error);
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    });

    return this.refreshPromise;
  }

  // Handle expired token (unchanged)
  handleExpiredToken() {
    this.removeToken();
    
    // Prevent redirect loop if already on login page
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/';
    }
  }

  // Validate and potentially refresh token (unchanged)
  async validateAndRefreshToken() {
    const token = this.getToken();
    
    if (!token) {
      this.handleExpiredToken();
      return null;
    }

    if (this.isTokenExpired(token)) {
      this.handleExpiredToken();
      return null;
    }

    // Try to refresh if token expires soon
    if (this.shouldRefresh(token)) {
      try {
        const newToken = await this.refreshToken();
        return newToken;
      } catch (error) {
        // If refresh fails, handle as expired
        this.handleExpiredToken();
        return null;
      }
    }

    return token;
  }

  // 🔥 NEW: Validate current session
  validateSession() {
    const token = this.getToken();
    const userData = this.getUserData();
    
    if (!token || !userData) {
      return false;
    }
    
    if (this.isTokenExpired(token)) {
      this.handleExpiredToken();
      return false;
    }
    
    return true;
  }

  // 🔥 NEW: Get session info for debugging
  getSessionInfo() {
    const token = this.getToken();
    const userData = this.getUserData();
    const rememberMe = this.isRememberMeEnabled();
    const tokenInfo = token ? this.getTokenExpirationInfo(token) : null;
    
    return {
      hasToken: !!token,
      hasUser: !!userData,
      rememberMe,
      tokenInfo,
      storageType: localStorage.getItem(this.tokenKey) ? 'localStorage' : 
                   sessionStorage.getItem(this.tokenKey) ? 'sessionStorage' : 'none'
    };
  }

  // 🔥 NEW: Get token expiration info
  getTokenExpirationInfo(token) {
    if (!token) return null;
    
    try {
      const payload = this.decodeToken(token);
      if (!payload) return null;
      
      const expirationDate = new Date(payload.exp * 1000);
      const currentTime = new Date();
      const timeUntilExpiration = expirationDate.getTime() - currentTime.getTime();
      
      return {
        expirationDate,
        timeUntilExpiration,
        isExpired: timeUntilExpiration <= 0,
        expiresInMinutes: Math.floor(timeUntilExpiration / (1000 * 60))
      };
    } catch (error) {
      return null;
    }
  }

  // 🔥 NEW: Clear all authentication data (alias for removeToken)
  clearAuth() {
    this.removeToken();
    
    // Dispatch custom event for app-wide handling
    window.dispatchEvent(new CustomEvent('authTokenExpired', {
      detail: { timestamp: new Date() }
    }));
  }
}

// Create singleton instance
const enhancedTokenManager = new EnhancedTokenManager();

// Enhanced fetch wrapper with automatic token refresh (unchanged)
export const authenticatedFetchWithRefresh = async (url, options = {}) => {
  try {
    // Validate and refresh token if needed
    const token = await enhancedTokenManager.validateAndRefreshToken();
    
    if (!token) {
      throw new Error('Authentication required');
    }

    const authOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    };

    const response = await fetch(url, authOptions);
    
    // If we get 401, try refreshing token once more
    if (response.status === 401 && !options._retried) {
      
      try {
        const newToken = await enhancedTokenManager.refreshToken();
        if (newToken) {
          // Retry with new token
          const retryOptions = {
            ...authOptions,
            headers: {
              ...authOptions.headers,
              'Authorization': `Bearer ${newToken}`,
            },
            _retried: true // Prevent infinite retry loop
          };
          
          return await fetch(url, retryOptions);
        }
      } catch (refreshError) {
        enhancedTokenManager.handleExpiredToken();
        throw new Error('Authentication failed');
      }
    }
    
    return response;
  } catch (error) {
    if (error.message === 'Failed to fetch') {
      throw new Error('Network error: Unable to connect to server');
    }
    throw error;
  }
};

// 🔥 ENHANCED: Auto-refresh setup with remember me awareness
export const setupAutoRefresh = () => {
  const checkAndRefreshToken = async () => {
    const token = enhancedTokenManager.getToken();
    
    if (!token) return;
    
    const tokenInfo = enhancedTokenManager.getTokenExpirationInfo(token);
    
    if (!tokenInfo) {
      enhancedTokenManager.handleExpiredToken();
      return;
    }
    
    // Refresh if token expires in less than 10 minutes
    if (tokenInfo.expiresInMinutes <= 10 && tokenInfo.expiresInMinutes > 0) {
      try {
        await enhancedTokenManager.refreshToken();
      } catch (error) {
      }
    } else if (tokenInfo.isExpired) {
      enhancedTokenManager.handleExpiredToken();
    }
  };

  // Check immediately
  checkAndRefreshToken();
  
  // Check every 4 minutes
  const interval = setInterval(checkAndRefreshToken, 4 * 60 * 1000);
  
  // Check when page becomes visible
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      checkAndRefreshToken();
    }
  });
  
  // Check when window gains focus
  window.addEventListener('focus', checkAndRefreshToken);
  
  return () => clearInterval(interval);
};

export { enhancedTokenManager };